import { invoke } from "@tauri-apps/api/core";
import { getCurrentWindow } from "@tauri-apps/api/window";
import { isDesktopOverlayWindow } from "./desktop-overlay-window";
import { refreshWindowOrigin } from "./use-window-origin";
import { waitForBrowserPaint } from "@/lib/browser-paint";

type NativeWorkspaceCommand =
  | "enter_denk_window"
  | "enter_denk_workspace_preview_window"
  | "exit_denk_workspace_preview_window"
  | "exit_workspace_window";

let nativeWorkspaceTransition: Promise<void> = Promise.resolve();
const NATIVE_GEOMETRY_SETTLE_TIMEOUT_MS = 420;
const NATIVE_GEOMETRY_SETTLE_POLL_MS = 16;

function finiteNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function closeEnough(a: number, b: number): boolean {
  return Math.abs(a - b) <= 2;
}

function sleep(ms: number) {
  return new Promise<void>((resolve) => window.setTimeout(resolve, ms));
}

function expectedNativeRect() {
  const x = finiteNumber(window.__DENKER_WORK_AREA_X__);
  const y = finiteNumber(window.__DENKER_WORK_AREA_Y__);
  const width = finiteNumber(window.__DENKER_WORK_AREA_WIDTH__);
  const height = finiteNumber(window.__DENKER_WORK_AREA_HEIGHT__);
  if (x === null || y === null || width === null || height === null)
    return null;
  return { x, y, width, height };
}

export function resolveMeasuredNativeWindowRect(
  position: { x: number; y: number },
  size: { width: number; height: number },
  scale: number,
) {
  if (!Number.isFinite(scale) || scale <= 0) return null;
  return {
    x: position.x / scale,
    y: position.y / scale,
    width: size.width / scale,
    height: size.height / scale,
  };
}

export function nativeWindowRectMatches(
  actual: { x: number; y: number; width: number; height: number } | null,
  expected: { x: number; y: number; width: number; height: number },
): boolean {
  return Boolean(
    actual &&
    closeEnough(actual.x, expected.x) &&
    closeEnough(actual.y, expected.y) &&
    closeEnough(actual.width, expected.width) &&
    closeEnough(actual.height, expected.height),
  );
}

async function readNativeWindowRect() {
  const windowRef = getCurrentWindow();
  const [position, size, scale] = await Promise.all([
    windowRef.outerPosition(),
    windowRef.innerSize(),
    windowRef.scaleFactor(),
  ]);
  return resolveMeasuredNativeWindowRect(position, size, scale);
}

async function waitForNativeGeometryToSettle() {
  if (!isDesktopOverlayWindow()) return;
  const start = performance.now();
  while (performance.now() - start < NATIVE_GEOMETRY_SETTLE_TIMEOUT_MS) {
    const expected = expectedNativeRect();
    if (!expected) break;
    try {
      const actual = await readNativeWindowRect();
      if (nativeWindowRectMatches(actual, expected)) {
        break;
      }
    } catch {
      break;
    }
    await sleep(NATIVE_GEOMETRY_SETTLE_POLL_MS);
  }
  await refreshWindowOrigin();
  await waitForBrowserPaint(undefined, 2);
}

function runAndSettleNativeWorkspaceTransition(
  command: NativeWorkspaceCommand,
) {
  if (!isDesktopOverlayWindow()) return Promise.resolve();
  nativeWorkspaceTransition = nativeWorkspaceTransition
    .catch(() => {
      // Keep the queue alive after a failed native transition.
    })
    .then(() => invoke(command))
    .then(
      () => {},
      () => {
        // Non-fatal: JS-side mode change already happened, the overlay
        // still renders correctly even if the OS window didn't resize.
      },
    )
    .then(() => waitForNativeGeometryToSettle());
  return nativeWorkspaceTransition;
}

export function enterNativeDenkWindow() {
  return runAndSettleNativeWorkspaceTransition("enter_denk_window");
}

export function enterNativeDenkWorkspacePreviewWindow() {
  return runAndSettleNativeWorkspaceTransition(
    "enter_denk_workspace_preview_window",
  );
}

export function exitNativeDenkWorkspacePreviewWindow() {
  return runAndSettleNativeWorkspaceTransition(
    "exit_denk_workspace_preview_window",
  );
}

export function exitNativeWorkspaceWindow() {
  return runAndSettleNativeWorkspaceTransition("exit_workspace_window");
}

export function syncNativeDenkCanvasCapture(active: boolean) {
  if (!isDesktopOverlayWindow()) return Promise.resolve();
  nativeWorkspaceTransition = nativeWorkspaceTransition
    .catch(() => {
      // Keep the queue alive after a failed native transition.
    })
    .then(() => invoke("sync_denk_canvas_capture", { active }))
    .then(
      () => {},
      () => {
        // Menu checkmark sync is best-effort; JS owns the interaction state.
      },
    );
  return nativeWorkspaceTransition;
}
